import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  X as XIcon,
  CheckCircle,
  AlertTriangle,
  Users,
  MessageSquare,
  Signal,
  Wifi,
  Battery,
} from "lucide-react";
import { Button, Typography, Chip, Box } from "@mui/material";
import Config from "../../Config";

const BlastModal = ({ open, onClose, blastData, onConfirm }) => {
  if (!open) return null;

  const {
    recipientCount,
    smsCredits,
    isBlastToAll,
    sender,
    previewMessage,
    campaignSlug,
  } = blastData;

  return (
    <AnimatePresence>
      <div className="overflow-y-auto fixed inset-0 z-50">
        <div className="flex justify-center items-center p-4 min-h-screen text-center">
          {/*  // Todo => Enhanced Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gradient-to-br backdrop-blur-md from-black/40 via-black/30 to-black/50"
            onClick={onClose}
          />

          {/* // Todo => Enhanced Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="overflow-hidden relative w-full max-w-4xl bg-white rounded-2xl border border-gray-100 shadow-2xl"
          >
            {/* // Todo =>Enhanced Header with gradient background */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
                padding: "8px 16px",
                color: "white",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* // Todo => Animated background elements */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 left-0 w-24 h-24 bg-white rounded-full animate-pulse -translate-x-12 -translate-y-12"></div>
                <div className="absolute right-0 bottom-0 w-16 h-16 bg-white rounded-full animate-pulse translate-x-8 translate-y-8" style={{ animationDelay: "1s" }}></div>
              </div>

              {/* // Todo => Close Button */}
              <button
                onClick={onClose}
                className="absolute top-3 right-3 p-1.5 rounded-full backdrop-blur-sm transition-all duration-200 hover:bg-white/20 bg-white/10 hover:scale-110"
              >
                <XIcon className="w-4 h-4 text-white" />
              </button>

              <div className="relative text-center">
                <motion.div 
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.2, type: "spring", damping: 20 }}
                  className="flex justify-center items-center mx-auto mb-2 w-10 h-8 rounded-xl shadow-lg backdrop-blur-sm bg-white/20"
                >
                  <Send className="w-5 h-5 text-white" />
                </motion.div>
                <Typography variant="h6" className="mb-1 font-bold text-white">
                  Confirm SMS Blast
                </Typography>
                <Typography variant="body2" className="text-sm text-white/90">
                  Review and send your campaign to the selected audience
                </Typography>
              </div>
            </Box>

            {/* // Todo => Enhanced Content */}
            <div className="px-6 py-6 pb-24 bg-gradient-to-br from-gray-50 to-gray-100 md:pb-6">
              <div className="grid grid-cols-1 gap-6 items-start lg:grid-cols-2">
                {/* // Todo => Left Column - Enhanced Blast Info */}
                <div className="space-y-2">
                  {/* // Todo => Enhanced Recipients Info Card */}
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="p-6 bg-white rounded-2xl border border-gray-200 shadow-lg transition-all duration-300 hover:shadow-xl"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex gap-3 items-center">
                        <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                          <Users className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-left">
                          <Typography
                            variant="h6"
                            className="mb-1 font-bold text-gray-800 text-md"
                          >
                            Target Audience
                          </Typography>
                          <Typography variant="body2" className="text-gray-600">
                            From: <span className="font-semibold text-blue-600">{sender}</span>
                          </Typography>
                        </div>
                      </div>
                      {isBlastToAll && (
                        <Chip
                          label="Full Blast"
                          variant="filled"
                          size="small"
                          sx={{
                            background: "linear-gradient(135deg, #ff6b6b, #ee5a52)",
                            color: "white",
                            fontWeight: "600",
                            fontSize: "0.75rem",
                            padding: "4px 8px",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(255, 107, 107, 0.3)",
                          }}
                        />
                      )}
                    </div>

                    <div className="px-4 py-1 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-xl border-2 border-blue-200 shadow-inner">
                      <div className="flex justify-between items-center">
                        <Typography
                          variant="body1"
                          className="font-semibold text-blue-700"
                        >
                          Total Recipients
                        </Typography>
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.5, type: "spring" }}
                        >
                          <Typography
                            variant="h4"
                            className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"
                          >
                            {recipientCount ? recipientCount.toLocaleString() : 0}
                          </Typography>
                        </motion.div>
                      </div>
                    </div>
                  </motion.div>

                  {/* // Todo => Enhanced Credits Info */}
                  <div className="space-y-4">
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                      className="p-4 bg-white rounded-xl border border-gray-200 shadow-lg transition-all duration-300 hover:shadow-xl"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex gap-3 items-center">
                          <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg shadow-lg">
                            <CheckCircle className="w-5 h-5 text-white" />
                          </div>
                          <Typography
                            variant="body1"
                            className="font-semibold text-gray-800"
                          >
                            SMS Credits Available
                          </Typography>
                        </div>
                        <Typography
                          variant="h5"
                          className="font-bold text-green-600"
                        >
                          {smsCredits ? smsCredits.toLocaleString() : 0}
                        </Typography>
                      </div>
                    </motion.div>

                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                      className="p-4 bg-white rounded-xl border border-gray-200 shadow-lg transition-all duration-300 hover:shadow-xl"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex gap-3 items-center">
                          <div className="p-2 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg shadow-lg">
                            <AlertTriangle className="w-5 h-5 text-white" />
                          </div>
                          <Typography
                            variant="body1"
                            className="font-semibold text-gray-800"
                          >
                            Credits Required
                          </Typography>
                        </div>
                        <Typography
                          variant="h5"
                          className="font-bold text-orange-600"
                        >
                          {recipientCount ? recipientCount.toLocaleString() : 0}
                        </Typography>
                      </div>
                    </motion.div>
                  </div>

                  {/* // Todo => Enhanced Desktop Actions - Hidden on mobile */}
                  
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={onConfirm}
                      startIcon={<Send className="w-4 h-4" />}
                      sx={{
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
                        padding: "12px 24px",
                        fontWeight: "700",
                        fontSize: "0.875rem",
                        textTransform: "none",
                        boxShadow: "0 6px 20px 0 rgba(102, 126, 234, 0.35)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #5a67d8 0%, #6b46c1 50%, #ec4899 100%)",
                          boxShadow: "0 8px 25px 0 rgba(102, 126, 234, 0.45)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Confirm & Send Blast
                    </Button>
                    <div className="hidden space-y-3 md:block">
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      onClick={onClose}
                      sx={{
                        borderRadius: "12px",
                        borderColor: "#E5E7EB",
                        color: "#374151",
                        padding: "12px 24px",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        textTransform: "none",
                        "&:hover": {
                          borderColor: "#D1D5DB",
                          backgroundColor: "#F9FAFB",
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>

                {/* Right Column - Enhanced Message Preview */}
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                  className="flex justify-center lg:justify-end"
                >
                  <div className="p-3 bg-white rounded-2xl border border-gray-200 shadow-xl">
                    <div className="flex gap-3 items-center mb-2">
                      <div className="p-1 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg shadow-lg">
                        <MessageSquare className="w-5 h-5 text-white" />
                      </div>
                      <Typography
                        variant="h7"
                        className="text-sm font-bold text-gray-800"
                      >
                        Message Preview
                      </Typography>
                    </div>

                    {/* Enhanced Phone Mockup */}
                    <div className="flex justify-center">
                      <motion.div 
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.8, type: "spring", damping: 20 }}
                        className="relative"
                      >
                        {/* Phone Frame with enhanced styling */}
                        <div className="relative p-2 w-64 h-[380px] bg-gradient-to-b from-gray-800 via-gray-900 to-black rounded-[2rem] shadow-2xl">
                          {/* Phone Screen Bezel */}
                          <div className="relative w-full h-full bg-black rounded-[1.5rem] p-1">
                            {/* Actual Screen */}
                            <div className="overflow-hidden relative w-full h-full bg-white rounded-[1.25rem]">
                              {/* Dynamic Island / Notch */}
                              <div className="flex absolute top-1 left-1/2 z-10 justify-center items-center w-16 h-4 bg-black rounded-full transform -translate-x-1/2">
                                <div className="mr-1 w-1.5 h-1.5 bg-gray-700 rounded-full"></div>
                                <div className="w-2 h-2 bg-gray-800 rounded-full"></div>
                              </div>

                              {/* Enhanced Status Bar */}
                              <div className="flex justify-between items-center px-4 py-2 pt-6 text-xs font-semibold text-gray-800 bg-gradient-to-r from-gray-50 to-gray-100">
                                <span className="font-bold">9:41</span>
                                <div className="flex gap-1 items-center">
                                  <Signal className="w-3 h-3" />
                                  <Wifi className="w-3 h-3" />
                                  <Battery className="w-4 h-2 rounded-sm border border-gray-600">
                                    <div className="w-full h-full bg-green-500 rounded-sm"></div>
                                  </Battery>
                                  <span className="text-xs">100%</span>
                                </div>
                              </div>

                              {/* Messages Header with enhanced styling */}
                              <div className="flex gap-3 items-center px-3 py-3 bg-gradient-to-r from-white to-gray-50 border-b border-gray-200 shadow-sm">
                                <motion.div 
                                  animate={{ scale: [1, 1.1, 1] }}
                                  transition={{ duration: 2, repeat: Infinity }}
                                  className="flex justify-center items-center w-8 h-8 bg-gradient-to-br from-red-500 to-pink-500 rounded-full shadow-lg"
                                >
                                  <MessageSquare className="w-4 h-4 text-white" />
                                </motion.div>
                                <div className="flex-1 text-left">
                                  <Typography
                                    variant="body2"
                                    className="font-bold text-gray-900"
                                  >
                                    {sender || "Dealboxx"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    className="text-xs font-medium text-green-500"
                                  >
                                    Online
                                  </Typography>
                                </div>
                              </div>

                              {/* Enhanced Messages Area */}
                              <div className="flex-1 p-3 space-y-3 bg-gradient-to-b from-gray-50 to-gray-100 min-h-[240px]">
                                {/* Enhanced Message Bubble */}
                                <motion.div 
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: 1 }}
                                  className="relative"
                                >
                                  <div className="p-3 mt-3 text-left bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl rounded-tl-md shadow-lg max-w-[85%]">
                                    <div className="flex gap-1 items-center mb-1">
                                      <div className="w-2 h-2 bg-white rounded-full opacity-80"></div>
                                      <Typography
                                        variant="caption"
                                        className="text-xs font-semibold text-white/90"
                                      >
                                        Dealboxx
                                      </Typography>
                                    </div>
                                    <div className="py-1">
                                      <Typography
                                        variant="caption"
                                        className="text-xs font-medium leading-relaxed text-white"
                                      >
                                        {previewMessage}
                                      </Typography>
                                    </div>
                                    <Typography
                                      variant="caption"
                                      className="block mt-1 text-xs font-medium text-blue-100 break-all"
                                    >
                                      {`${Config.consumerDomain}/product/${campaignSlug}`}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      className="block mt-1 text-xs text-right text-white/70"
                                    >
                                      Now
                                    </Typography>
                                  </div>
                                  {/* Message tail */}
                                  <div className="absolute -left-1 top-3 w-0 h-0 border-t-4 border-r-4 border-b-4 border-r-blue-500 border-t-transparent border-b-transparent"></div>
                                </motion.div>

                                {/* Enhanced Typing Indicator */}
                                <motion.div 
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ delay: 1.5 }}
                                  className="flex justify-end"
                                >
                                  <div className="px-3 py-2 bg-white rounded-xl rounded-br-md border border-gray-200 shadow-md">
                                    <div className="flex gap-1">
                                      <motion.div 
                                        animate={{ y: [0, -3, 0] }}
                                        transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                                        className="w-1.5 h-1.5 bg-gray-400 rounded-full"
                                      />
                                      <motion.div 
                                        animate={{ y: [0, -3, 0] }}
                                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                                        className="w-1.5 h-1.5 bg-gray-400 rounded-full"
                                      />
                                      <motion.div 
                                        animate={{ y: [0, -3, 0] }}
                                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                                        className="w-1.5 h-1.5 bg-gray-400 rounded-full"
                                      />
                                    </div>
                                  </div>
                                </motion.div>
                              </div>

                              {/* Enhanced Message Input Area */}
                              <div className="p-3 bg-white border-t border-gray-200">
                                <div className="flex gap-2 items-center">
                                  <div className="flex-1 px-3 py-2 bg-gray-100 rounded-full border border-gray-200">
                                    <Typography
                                      variant="caption"
                                      className="text-xs text-gray-500"
                                    >
                                      Message
                                    </Typography>
                                  </div>
                                  <motion.div 
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    className="flex justify-center items-center w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-lg cursor-pointer"
                                  >
                                    <Send className="w-3 h-3 text-white" />
                                  </motion.div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Enhanced Phone Reflection */}
                          <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-[2rem] pointer-events-none"></div>
                          
                          {/* Phone Shadow */}
                          <div className="absolute -inset-1 bg-gradient-to-b from-transparent via-black/5 to-black/20 rounded-[2.5rem] -z-10 blur-lg"></div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Mobile Sticky Actions - Only visible on mobile */}
            <div className="fixed right-0 bottom-0 left-0 z-50 p-4 bg-white border-t border-gray-200 shadow-lg md:hidden">
              <div className="flex gap-3">
                <Button
                  variant="outlined"
                  size="large"
                  onClick={onClose}
                  sx={{
                    flex: "0 0 auto",
                    minWidth: "100px",
                    borderRadius: "12px",
                    borderColor: "#E5E7EB",
                    color: "#374151",
                    padding: "12px 20px",
                    fontWeight: "600",
                    fontSize: "0.875rem",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#D1D5DB",
                      backgroundColor: "#F9FAFB",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={onConfirm}
                  startIcon={<Send className="w-4 h-4" />}
                  sx={{
                    flex: "1",
                    borderRadius: "12px",
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
                    padding: "12px 24px",
                    fontWeight: "700",
                    fontSize: "0.875rem",
                    textTransform: "none",
                    boxShadow: "0 6px 20px 0 rgba(102, 126, 234, 0.35)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5a67d8 0%, #6b46c1 50%, #ec4899 100%)",
                      boxShadow: "0 8px 25px 0 rgba(102, 126, 234, 0.45)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Confirm & Send
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default BlastModal;
